import React, { useState, useEffect } from 'react';
import { useFirebase } from '../Auth/FirebaseProvider';
import { foodTruckService } from '../../services/foodTruckService';
import {
  Box,
  Typography,
  Button,
  Chip,
  Drawer,
  Alert,
  Paper,
} from '@mui/material';
import {
  FaStore,
  FaEdit,
  FaPhone,
  FaGlobe,
  FaInstagram,
  FaFacebook,
  FaTwitter,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import TruckInfoEditPanel from './TruckInfoEditPanel';

function FoodTruckEditor() {
  const { foodTruckId, loading: contextLoading } = useFirebase();
  const [foodTruck, setFoodTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [services, setServices] = useState(null);

  useEffect(() => {
    if (!foodTruckId || contextLoading) return;

    const initializeServices = () => {
      try {
        const foodTruckSrv = foodTruckService.getInstance(foodTruckId);
        setServices({ foodTruckSrv });
        return { foodTruckSrv };
      } catch (error) {
        console.error("Error initializing services:", error);
        throw error;
      }
    };

    const loadFoodTruck = async () => {
      try {
        setLoading(true);
        const { foodTruckSrv } = initializeServices();
        const data = await foodTruckSrv.getFoodTruck();

        setFoodTruck(data);
      } catch (error) {
        console.error("Error loading food truck:", error);
        setError("Erreur lors du chargement des données du food truck");
        toast.error("Erreur lors du chargement des données du food truck");
      } finally {
        setLoading(false);
      }
    };

    loadFoodTruck();
  }, [foodTruckId, contextLoading]);


  const formatDescription = (text) => {
    if (!text) return '';
    // Gère les retours à la ligne
    const withLineBreaks = text.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {/* Gère le texte en gras entre ** */}
        {line.split(/(\*\*.*?\*\*)/).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j}>{part.slice(2, -2)}</strong>;
          }
          return part;
        })}
      </React.Fragment>
    ));
    return withLineBreaks;
  };


  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'instagram': return <FaInstagram />;
      case 'facebook': return <FaFacebook />;
      case 'twitter': return <FaTwitter />;
      case 'web': return <FaGlobe />;
      default: return null;
    }
  };

  const handleUpdate = async (updatedData) => {
    if (!services?.foodTruckSrv) return;

    try {
      setLoading(true);
      await services.foodTruckSrv.updateFoodTruck(updatedData);
      setFoodTruck(updatedData);
      setIsEditing(false);
      toast.success("Restaurant mis à jour avec succès !");
    } catch (error) {
      console.error("Error updating food truck:", error);
      toast.error("Erreur lors de la mise à jour du food truck");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!foodTruck) return null;

  return (
    <Box sx={{ p: 0 }}>
      {/* Header de la page */}
      <Box sx={{ 
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        p: 3,
        mb: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaStore size={20} color="white" />
              </Box>
              Informations du restaurant
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                mt: 0.5
              }}
            >
              Gérez les informations générales et contacts
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<FaEdit />}
            onClick={() => setIsEditing(true)}
            sx={{ borderRadius: 2 }}
          >
            Modifier
          </Button>
        </Box>
      </Box>

      {/* Contenu principal */}
      <Box sx={{ px: 3 }}>

        {/* Sections de contenu */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Section Contact */}
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaPhone size={16} />
                Contact
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <Chip
                  icon={<FaPhone />}
                  label={foodTruck.phoneNumber}
                  variant="outlined"
                  color="primary"
                  sx={{ height: 32 }}
                />
                {foodTruck.socialMedia?.map((social, index) => (
                  <Chip
                    key={index}
                    icon={getSocialIcon(social.platform)}
                    label={social.platform.charAt(0).toUpperCase() + social.platform.slice(1)}
                    component="a"
                    href={social.url}
                    target="_blank"
                    clickable
                    variant="outlined"
                    color="primary"
                    sx={{ height: 32 }}
                  />
                ))}
              </Box>
            </Box>
          </Paper>

          {/* Section Description */}
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaEdit size={16} />
                Description
              </Typography>
              <Alert severity="info" sx={{ mt: 2, mb: 0 }}>
                La description sera directement affichée dans l'application mobile.
              </Alert>
            </Box>
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" component="div" sx={{
                lineHeight: 1.7,
                '& strong': {
                  color: 'primary.main',
                  fontWeight: 600
                }
              }}>
                {formatDescription(foodTruck.description)}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Edit Panel */}
      <Drawer
        anchor="right"
        open={isEditing}
        onClose={() => setIsEditing(false)}
        PaperProps={{
          sx: { width: 500 }
        }}
      >
        <TruckInfoEditPanel
          foodTruck={foodTruck}
          onClose={() => setIsEditing(false)}
          onUpdate={handleUpdate}
        />
      </Drawer>
    </Box>
  );
}

export default FoodTruckEditor;